import { createContext, useMemo, useReducer, useContext, ReactNode } from "react"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"

const PREFERENCE_SUBTYPE = "graph-exposure"
const PREFERENCE_FIELDS = ["selectedCategory"]

interface ExposureState {
  selectedCategory: string
}

const DEFAULT_STATE: ExposureState = {
  selectedCategory: "total",
}

interface SetSelectedCategoryAction {
  type: "setSelectedCategory"
  selectedCategory: string
}

interface SetPreferencesAction {
  type: "setPreferences"
  preferences: Partial<ExposureState> | null
}

type ExposureAction = SetSelectedCategoryAction | SetPreferencesAction

interface ExposureContextUpdaters {
  setSelectedCategory: (selectedCategory: string) => void
}

export interface ExposureContextValue extends ExposureState, ExposureContextUpdaters {
  isPreferencesLoaded: boolean
}

function reducer(current: ExposureState, action: ExposureAction): ExposureState {
  return produce(current, draft => {
    switch (action.type) {
      case "setSelectedCategory":
        draft.selectedCategory = action.selectedCategory
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
    }
  })
}

function setPreferences(preferences: Partial<ExposureState>): SetPreferencesAction {
  return { type: "setPreferences", preferences }
}

export const EXPOSURE_CONTEXT = createContext<ExposureContextValue | undefined>(undefined)

export function useExposureContext(): ExposureContextValue {
  const ctx = useContext(EXPOSURE_CONTEXT)
  if (ctx === undefined) {
    throw new Error("useExposureContext must be used within an ExposureContext provider")
  }
  return ctx
}

interface ExposureContextProps {
  children: ReactNode
}

export default function ExposureContext({ children }: ExposureContextProps) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)
  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo<ExposureContextUpdaters>(() => ({
    setSelectedCategory: (selectedCategory) => dispatch({ type: "setSelectedCategory", selectedCategory }),
  }), [dispatch])

  const contextValue = useMemo<ExposureContextValue>(() => ({
    ...state,
    ...contextUpdaters,
    isPreferencesLoaded,
  }), [state, contextUpdaters, isPreferencesLoaded])

  return <EXPOSURE_CONTEXT.Provider value={contextValue}>
    {children}
  </EXPOSURE_CONTEXT.Provider>
}
